import React, { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const SERVICES = [
  { id: "bookkeeping", label: "Bookkeeping" },
  { id: "customer_support", label: "Customer Support" },
  { id: "virtual_assistance", label: "Virtual Assistance" },
  { id: "marketing_assistance", label: "Marketing Assistance" },
  { id: "executive_assistance", label: "Executive Assistance" },
  { id: "not_sure", label: "Not sure yet" },
];

export default function ContactForm({ compact = false, testid = "contact-form" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_website: "",
    services: [],
    message: "",
    consent: false,
  });
  const [status, setStatus] = useState({ loading: false, ok: null, err: null });
  const [website, setWebsite] = useState(""); // honeypot — must stay empty
  const turnstileRef = useRef(null);
  const formRenderedAt = useRef(Date.now());

  // Re-stamp render time when the component mounts (per page visit).
  useEffect(() => {
    formRenderedAt.current = Date.now();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (id) =>
    update("services", form.services.includes(id) ? form.services.filter((s) => s !== id) : [...form.services, id]);

  // Invisible Turnstile: trigger execute(), resolve the resulting token via callbacks.
  const pendingResolve = useRef(null);
  const getTurnstileToken = () =>
    new Promise((resolve) => {
      const widget = turnstileRef.current;
      if (!TURNSTILE_SITE_KEY || !widget) {
        resolve(""); // not configured / not mounted — backend fails open
        return;
      }
      pendingResolve.current = resolve;
      try {
        widget.reset();
        widget.execute();
      } catch (err) {
        resolve("");
      }
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.consent) {
      setStatus({ loading: false, ok: null, err: "Please fill name, email and agree to the policy." });
      return;
    }
    setStatus({ loading: true, ok: null, err: null });
    try {
      const turnstileToken = await getTurnstileToken();
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          website,
          turnstileToken,
          formRenderedAt: formRenderedAt.current,
        }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setStatus({ loading: false, ok: "Thanks! We'll be in touch within 4 hours.", err: null });
      setForm({ name: "", email: "", phone: "", company_website: "", services: [], message: "", consent: false });
    } catch (err) {
      setStatus({ loading: false, ok: null, err: "Something went wrong. Please try again." });
    }
  };

  return (
    <form onSubmit={submit} data-testid={testid} className="aidify-card p-6 md:p-8">
      <div className="text-xs uppercase tracking-[0.2em] text-forest-400 mb-2">Enquire Now</div>
      <h3 className="font-display font-extrabold text-3xl md:text-4xl text-forest-600 mb-6">
        Tell us about your <span className="font-serif-italic font-normal">Business.</span>
      </h3>

      {/* Honeypot — hidden from real users; bots that fill it are silently rejected */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor="cf-website-hp">Website</label>
        <input
          id="cf-website-hp"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-forest-400 mb-1">Name *</label>
          <input data-testid="cf-name" required className="aidify-input" value={form.name} onChange={(e) => update("name", e.target.value)} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-forest-400 mb-1">Email *</label>
          <input data-testid="cf-email" required type="email" className="aidify-input" value={form.email} onChange={(e) => update("email", e.target.value)} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-forest-400 mb-1">Phone</label>
          <input data-testid="cf-phone" className="aidify-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-forest-400 mb-1">Company / Website</label>
          <input data-testid="cf-company" className="aidify-input" value={form.company_website} onChange={(e) => update("company_website", e.target.value)} />
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.18em] text-forest-400 mb-3">What do you need help with?</div>
        <div className="flex flex-wrap gap-2">
          {SERVICES.map((s) => {
            const active = form.services.includes(s.id);
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => toggleService(s.id)}
                data-testid={`cf-service-${s.id}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  active ? "bg-forest-600 text-cream-100 border-forest-600" : "bg-transparent text-forest-500 border-forest-500/25 hover:border-forest-500/60"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-[11px] uppercase tracking-[0.18em] text-forest-400 mb-1">Tell us more — Optional</label>
        <textarea
          rows={compact ? 3 : 4}
          data-testid="cf-message"
          className="aidify-input resize-none"
          placeholder="What does success looks like? Tools you use? Timeline?"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
        />
      </div>

      <label className="mt-6 flex items-start gap-3 text-sm text-forest-500 cursor-pointer">
        <input
          data-testid="cf-consent"
          type="checkbox"
          className="mt-1"
          checked={form.consent}
          onChange={(e) => update("consent", e.target.checked)}
        />
        <span>I agree to be contacted by aidify in accordance with their privacy policy.</span>
      </label>

      {/* Invisible Turnstile — executed on submit, no visible widget */}
      {TURNSTILE_SITE_KEY && (
        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_SITE_KEY}
          options={{ execution: "execute", appearance: "interaction-only", size: "invisible" }}
          onSuccess={(token) => {
            if (pendingResolve.current) {
              pendingResolve.current(token);
              pendingResolve.current = null;
            }
          }}
          onError={() => {
            if (pendingResolve.current) {
              pendingResolve.current("");
              pendingResolve.current = null;
            }
          }}
          onExpire={() => {
            if (turnstileRef.current) turnstileRef.current.reset();
          }}
        />
      )}

      <div className="mt-6 pt-5 border-t border-forest-500/15 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-forest-400/80 max-w-md">
          No spam. No pushy follow-ups. Just a useful conversation about whether we're a fit.
        </p>
        <button data-testid="cf-submit" disabled={status.loading} className="btn-primary">
          {status.loading ? "Verifying…" : "Send Message"} <Send size={14} />
        </button>
      </div>

      {status.ok && <p className="mt-4 text-sm text-sage-500" data-testid="cf-success">{status.ok}</p>}
      {status.err && <p className="mt-4 text-sm text-red-700" data-testid="cf-error">{status.err}</p>}
    </form>
  );
}
